import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Wand2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 1 — Promoter Onboarding (PM/Admin desktop form).
 *
 * Atomically creates a promoter profile + linked sales_promoter login. PM can
 * type a password manually OR click "Generate password" — the generated value
 * is shown ONCE so it can be shared with the field promoter.
 *
 * Citizenship front/back and bank details are mandatory (server-validated too).
 */
const initialForm = {
  name: "", phone: "", email: "", date_of_birth: "", gender: "",
  address: "", emergency_contact_name: "", emergency_contact_phone: "",
  photo_url: "", citizenship_front_url: "", citizenship_back_url: "",
  bank_name: "", bank_account_number: "", bank_account_holder_name: "",
  promoter_type: "incentive_only", monthly_salary: "", incentive_eligible: true,
  // Phase 6.1.C — persona ('regular' | 'self_promoter')
  persona: "regular",
  assigned_dealer_id: "", assigned_dealer_name: "",
  assigned_product_manager_id: "", assigned_sales_manager_id: "", assigned_sales_head_id: "",
  province: "", area: "", joining_date: "", remarks: "",
  password: "",
};

export default function PromoterOnboarding() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [opts, setOpts] = useState({ dealers: [], provinces: [], product_managers: [], sales_managers: [], sales_heads: [], promoter_types: [], genders: [] });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [generatedOnce, setGeneratedOnce] = useState(false);

  useEffect(() => {
    api.get("/promoter/options")
      .then(({ data }) => setOpts(data))
      .catch((e) => toast.error("Could not load form options", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onDealerChange = (id) => {
    const d = opts.dealers.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      assigned_dealer_id: id,
      assigned_dealer_name: d?.name || "",
      area: f.area || d?.area || "",
      province: f.province || d?.province || "",
    }));
  };

  const generatePassword = async () => {
    try {
      const { data } = await api.post("/promoter/utils/generate-password");
      setForm((f) => ({ ...f, password: data.password }));
      setShowPassword(true);
      setGeneratedOnce(true);
      toast.success("Password generated — please copy/share it now.");
    } catch (e) {
      toast.error("Could not generate password", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    if (!form.citizenship_front_url) { toast.error("Citizenship front photo is required"); return; }
    if (!form.citizenship_back_url) { toast.error("Citizenship back photo is required"); return; }
    if (!form.bank_name.trim() || !form.bank_account_number.trim() || !form.bank_account_holder_name.trim()) {
      toast.error("Bank name, account number, and holder name are required"); return;
    }
    if (!form.password || form.password.length < 8) {
      toast.error("Password must be at least 8 characters (type one or click Generate)"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || null,
        monthly_salary: form.monthly_salary === "" ? null : Number(form.monthly_salary),
        active: true,
      };
      const { data } = await api.post("/promoter/promoters", payload);
      toast.success("Promoter onboarded", {
        description: `${data.promoter_id} — ${data.name}`,
      });
      navigate(`/promoter/manage`);
    } catch (err) {
      toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="promoter-onboarding-form">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Onboard Promoter</h1>
          <p className="text-sm text-slate-500 mt-1">Creates the promoter profile AND their login account in one step.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/promoter/manage")}
                  data-testid="onboard-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="onboard-submit-btn">
            {submitting ? "Saving..." : "Onboard Promoter"}
          </Button>
        </div>
      </div>

      <Section title="Personal Details">
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="onboard-name" />
        </Field>
        <Field label="Phone / Username" required>
          <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="onboard-phone"
                 placeholder="+977-98XXXXXXXX (also used as login username)" />
        </Field>
        <Field label="Email (optional)">
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="onboard-email" />
        </Field>
        <Field label="Date of Birth">
          <Input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} />
        </Field>
        <Field label="Gender">
          <Select value={form.gender} onValueChange={(v) => update("gender", v)}>
            <SelectTrigger data-testid="onboard-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>{opts.genders.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Address">
          <Textarea rows={2} value={form.address} onChange={(e) => update("address", e.target.value)} />
        </Field>
        <Field label="Emergency Contact Name">
          <Input value={form.emergency_contact_name} onChange={(e) => update("emergency_contact_name", e.target.value)} />
        </Field>
        <Field label="Emergency Contact Phone">
          <Input value={form.emergency_contact_phone} onChange={(e) => update("emergency_contact_phone", e.target.value)} />
        </Field>
        <Field label="Joining Date">
          <Input type="date" value={form.joining_date} onChange={(e) => update("joining_date", e.target.value)} />
        </Field>
      </Section>

      <Section title="Documents (mandatory)">
        <Field label="Profile Photo">
          <FilePickField value={form.photo_url} onChange={(v) => update("photo_url", v)} testId="onboard-photo" />
        </Field>
        <Field label="Citizenship Front" required>
          <FilePickField value={form.citizenship_front_url} onChange={(v) => update("citizenship_front_url", v)} testId="onboard-cit-front" />
        </Field>
        <Field label="Citizenship Back" required>
          <FilePickField value={form.citizenship_back_url} onChange={(v) => update("citizenship_back_url", v)} testId="onboard-cit-back" />
        </Field>
      </Section>

      <Section title="Bank Details (mandatory)">
        <Field label="Bank Name" required>
          <Input value={form.bank_name} onChange={(e) => update("bank_name", e.target.value)} data-testid="onboard-bank-name" />
        </Field>
        <Field label="Account Number" required>
          <Input value={form.bank_account_number} onChange={(e) => update("bank_account_number", e.target.value)} data-testid="onboard-bank-acct" />
        </Field>
        <Field label="Account Holder Name" required>
          <Input value={form.bank_account_holder_name} onChange={(e) => update("bank_account_holder_name", e.target.value)} data-testid="onboard-bank-holder" />
        </Field>
      </Section>

      <Section title="Pay Model">
        <Field label="Persona" required>
          <Select value={form.persona} onValueChange={(v) => update("persona", v)}>
            <SelectTrigger data-testid="onboard-persona"><SelectValue placeholder="Select persona" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="regular">Regular Promoter</SelectItem>
              <SelectItem value="self_promoter">Self Promoter (Dealer staff)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Promoter Type" required>
          <Select value={form.promoter_type} onValueChange={(v) => update("promoter_type", v)}>
            <SelectTrigger data-testid="onboard-promoter-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="salaried">Salaried</SelectItem>
              <SelectItem value="incentive_only">Incentive only</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Monthly Salary (NPR)">
          <Input type="number" value={form.monthly_salary} onChange={(e) => update("monthly_salary", e.target.value)}
                 placeholder={form.persona === "self_promoter" ? "Self-promoters earn only from schemes" : "Leave blank for incentive-only"}
                 disabled={form.persona === "self_promoter"}
                 data-testid="onboard-monthly-salary" />
        </Field>
        <Field label="Incentive Eligible">
          <label className="flex items-center gap-2 h-9 text-sm text-slate-700">
            <Checkbox checked={form.incentive_eligible}
                       onCheckedChange={(v) => update("incentive_eligible", !!v)}
                       disabled={form.persona === "self_promoter"}
                       data-testid="onboard-incentive-eligible" />
            <span>{form.persona === "self_promoter"
              ? "Self promoters only earn from sell-out schemes"
              : "Eligible for incentive payouts"}</span>
          </label>
        </Field>
      </Section>

      <Section title="Assignments">
        <Field label="Dealer / Counter">
          <Select value={form.assigned_dealer_id} onValueChange={onDealerChange}>
            <SelectTrigger data-testid="onboard-dealer"><SelectValue placeholder="Select dealer" /></SelectTrigger>
            <SelectContent>{opts.dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Assigned Product Manager">
          <Select value={form.assigned_product_manager_id} onValueChange={(v) => update("assigned_product_manager_id", v)}>
            <SelectTrigger data-testid="onboard-pm">
              <SelectValue placeholder={opts.product_managers.length ? "Select PM" : "No PMs available"} />
            </SelectTrigger>
            <SelectContent>{opts.product_managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Assigned Sales Manager">
          <Select value={form.assigned_sales_manager_id} onValueChange={(v) => update("assigned_sales_manager_id", v)}>
            <SelectTrigger data-testid="onboard-sm">
              <SelectValue placeholder={opts.sales_managers.length ? "Select SM" : "No SMs available"} />
            </SelectTrigger>
            <SelectContent>{opts.sales_managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Assigned Sales Head">
          <Select value={form.assigned_sales_head_id} onValueChange={(v) => update("assigned_sales_head_id", v)}>
            <SelectTrigger data-testid="onboard-sh">
              <SelectValue placeholder={opts.sales_heads.length ? "Select Sales Head" : "No Sales Heads available"} />
            </SelectTrigger>
            <SelectContent>{opts.sales_heads.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Province">
          <Select value={form.province} onValueChange={(v) => update("province", v)}>
            <SelectTrigger data-testid="onboard-province"><SelectValue placeholder="Select province" /></SelectTrigger>
            <SelectContent>{opts.provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Area / Locality">
          <Input value={form.area} onChange={(e) => update("area", e.target.value)} data-testid="onboard-area" />
        </Field>
      </Section>

      <Section title="Login Credentials">
        <Field label="Password" required>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input type={showPassword ? "text" : "password"} value={form.password}
                     onChange={(e) => { setGeneratedOnce(false); update("password", e.target.value); }}
                     placeholder="Type one or click Generate" minLength={8}
                     data-testid="onboard-password" />
              <button type="button" onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="onboard-password-toggle">
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <Button type="button" variant="outline" onClick={generatePassword}
                    data-testid="onboard-generate-password-btn">
              <Wand2 size={14} className="mr-1" /> Generate
            </Button>
          </div>
          {generatedOnce && form.password && (
            <div className="text-xs text-amber-700 mt-1.5" data-testid="onboard-password-generated-hint">
              Copy and share this password now — it won&apos;t be shown again after save.
            </div>
          )}
        </Field>
        <Field label="Remarks">
          <Textarea rows={2} value={form.remarks} onChange={(e) => update("remarks", e.target.value)} />
        </Field>
      </Section>
    </form>
  );
}

// ----- helpers -----
function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="font-heading font-semibold text-slate-900 mb-4 text-lg">{title}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function FilePickField({ value, onChange, testId, accept = "image/*,application/pdf" }) {
  const [busy, setBusy] = useState(false);
  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Max 4 MB"); return; }
    const reader = new FileReader();
    setBusy(true);
    reader.onload = () => { onChange(reader.result); setBusy(false); };
    reader.onerror = () => { toast.error("Could not read file"); setBusy(false); };
    reader.readAsDataURL(file);
  };
  const isImage = value && value.startsWith && value.startsWith("data:image/");
  return (
    <div className="space-y-2">
      <input type="file" accept={accept} onChange={onPick}
             className="block w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-200 file:text-slate-800"
             data-testid={testId} />
      {busy && <div className="text-xs text-slate-500">Reading file…</div>}
      {isImage && (
        <div className="flex items-start gap-2">
          <img src={value} alt="preview" className="h-16 w-16 object-cover rounded border" />
          <button type="button" onClick={() => onChange("")} className="text-xs text-rose-600 hover:underline"
                  data-testid={`${testId}-clear`}>Remove</button>
        </div>
      )}
    </div>
  );
}
